import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SanitizeHTML from 'common/components/Elements/SanitizeHTML/SanitizeHTML';
import UnitDropdown from 'common/components/Elements/UnitDropdown/UnitDropdown';
import type * as I from 'core/api/coreApiClient';
import { inputStyles, shortInputWidth } from 'core/utilities/styles/input.styles';
import theme, { sx } from 'core/utilities/theme';
import React from 'react';

import classes from './InputUnit.style';

interface ComponentProps {
  value: string;
  unit: string;
  units?: I.Unit[];
  longFormStyle?: boolean;
  disabled?: boolean;
  sideLabel?: boolean;
  shortInput?: boolean;
  inputAdornmentWidth: number;
  onChange?: (unitId: string) => void;
  inTable?: boolean;
}

/**
 * Position the given unit just after the value within the form without interacting with the input field or value.
 */
function InputUnit({
  unit, units, value, longFormStyle, disabled, sideLabel, shortInput,
  inputAdornmentWidth, onChange, inTable,
}: ComponentProps) {
  let marginTop = theme.spacing(1);
  if (longFormStyle && !sideLabel) marginTop = theme.spacing(3);
  const styles = inputStyles({
    longFormStyle, sideLabel, errorMsg: [], inTable,
  });

  let unitLabel: string | undefined = unit;
  let unitHtmlLabel: string | undefined;
  if (units && units.length > 0) {
    const selectedUnit = units.find((u) => u.id === unit);
    if (selectedUnit) {
      if (selectedUnit.htmlLabel) {
        unitLabel = undefined;
        unitHtmlLabel = selectedUnit.htmlLabel;
      } else {
        unitLabel = selectedUnit.label;
      }
    }
  }

  return (
    <Typography
      component="span"
      sx={sx({ marginTop, width: '100%' }, classes.root)}
    >
      <Typography
        component="span"
        sx={sx(
          {
            float: shortInput ? 'left' : undefined,
            // Shorten the input by the size of end adornment (unit and when present clear icon, help icon etc.)
            // and the space between to the input and end adornment
            maxWidth: `calc(${shortInput ? shortInputWidth : '100%'} - ${inputAdornmentWidth + 8}px)`,
          },
          styles.inputRoot,
          classes.value,
        )}
      >
        {value}
      </Typography>
      <Typography
        component="span"
        sx={sx(
          {
            verticalAlign: 'middle',
            color: disabled ? theme.palette.text.disabled : 'inherit',
            // Fix Firefox render where the unit text was one pixel down
            lineHeight: 1.6,
          },
          styles.inputRoot,
          classes.unit,
        )}
      >
        {unitHtmlLabel && <SanitizeHTML html={unitHtmlLabel} />}
        {!unitHtmlLabel && unitLabel}
      </Typography>

      {units && units.length > 1 && onChange && (
        <Box sx={classes.units}>
          <UnitDropdown
            currentUnitId={unit}
            units={units}
            onChange={onChange}
          />
        </Box>
      )}
    </Typography>
  );
}

export default InputUnit;
